import { useEffect, useRef } from "react";

const HOVER_SELECTOR = [
  "a[href]",
  "button",
  'input:not([type="hidden"])',
  "textarea",
  "select",
  "summary",
  '[role="button"]',
  '[data-mii-cursor="link"]',
].join(",");

/**
 * Cursor estilo dny19.com: cruz full-viewport + retículo SVG que segue o ponteiro
 * com suavização. Sem WebGL (fluido do original). Desactivado com reduced motion
 * ou sem pointer fino.
 */
export function CustomCursor() {
  const targetRef = useRef({ x: 0, y: 0 });
  const posRef = useRef({ x: 0, y: 0 });
  const hoverRef = useRef(false);
  const rafRef = useRef(0);

  const hLineRef = useRef<HTMLDivElement>(null);
  const vLineRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (reduced || !fine) return;

    const root = document.documentElement;
    root.classList.add("mii-custom-cursor");

    const setVisible = (v: boolean) => {
      const o = v ? "1" : "0";
      if (hLineRef.current) hLineRef.current.style.opacity = o;
      if (vLineRef.current) vLineRef.current.style.opacity = o;
      if (wrapRef.current) wrapRef.current.style.opacity = o;
    };

    const onMove = (e: MouseEvent) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
      const el = e.target as Element | null;
      hoverRef.current = Boolean(el?.closest(HOVER_SELECTOR));
      setVisible(true);
    };

    const onLeave = () => setVisible(false);

    const onOver = (e: MouseEvent) => {
      const el = e.target as Element | null;
      hoverRef.current = Boolean(el?.closest(HOVER_SELECTOR));
    };

    const tick = () => {
      const lerp = hoverRef.current ? 0.24 : 0.15;
      const { x: tx, y: ty } = targetRef.current;
      const { x: px, y: py } = posRef.current;
      posRef.current.x = px + (tx - px) * lerp;
      posRef.current.y = py + (ty - py) * lerp;
      const { x, y } = posRef.current;

      if (hLineRef.current) hLineRef.current.style.top = `${y}px`;
      if (vLineRef.current) vLineRef.current.style.left = `${x}px`;
      if (wrapRef.current) {
        wrapRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      }
      if (markRef.current) {
        const scale = hoverRef.current ? 1.16 : 1;
        const opacity = hoverRef.current ? 1 : 0.9;
        markRef.current.style.transform = `translate(-50%, -50%) scale(${scale})`;
        markRef.current.style.opacity = String(opacity);
        markRef.current.style.color = hoverRef.current
          ? "var(--mii-accent)"
          : "var(--mii-ink)";
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseover", onOver, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      root.classList.remove("mii-custom-cursor");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseover", onOver);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div aria-hidden className="mii-cursor-root pointer-events-none fixed inset-0 z-[10000]">
      <div
        ref={hLineRef}
        className="fixed left-0 right-0 z-0 h-px bg-ink/[0.16] opacity-0 will-change-[top,opacity]"
        style={{ top: 0 }}
      />
      <div
        ref={vLineRef}
        className="fixed top-0 bottom-0 z-0 w-px bg-ink/[0.16] opacity-0 will-change-[left,opacity]"
        style={{ left: 0 }}
      />
      <div
        ref={wrapRef}
        className="fixed left-0 top-0 z-10 opacity-0 will-change-transform"
        style={{ transform: "translate3d(0,0,0)" }}
      >
        <div
          ref={markRef}
          className="text-ink will-change-[transform,opacity]"
          style={{
            transform: "translate(-50%, -50%) scale(1)",
            opacity: 0.9,
          }}
        >
          <svg
            width="41"
            height="41"
            viewBox="0 0 41 41"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-[0_0_14px_rgb(0_0_0/0.4)]"
            aria-hidden
          >
            <path
              d="M0.5 8.5V0.5H8.5M32.5 0.5H40.5V8.5M40.5 32.5V40.5H32.5M8.5 40.5H0.5V32.5M20.5 10.5V30.5M10.5 20.5H30.5"
              stroke="currentColor"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
